'use client';

import React from 'react';
import { CheckCircle2, MapPin, ShieldCheck, Wrench, Users } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';

const SERVICES = [
{ id: 'svc-001', name: 'Boiler Service & Repair', price: 'From £89', popular: true },
{ id: 'svc-002', name: 'Emergency Plumbing Callout', price: 'From £120', popular: true },
{ id: 'svc-003', name: 'Bathroom Installation', price: 'From £800', popular: false },
{ id: 'svc-004', name: 'Gas Safety Certificate', price: '£65', popular: false },
{ id: 'svc-005', name: 'Radiator Replacement', price: 'From £150', popular: false },
{ id: 'svc-006', name: 'Underfloor Heating', price: 'POA', popular: false },
{ id: 'svc-007', name: 'Leak Detection & Repair', price: 'From £95', popular: false },
{ id: 'svc-008', name: 'Power Flushing', price: 'From £300', popular: false }];


const CERTIFICATIONS = [
{ id: 'cert-001', name: 'Gas Safe Registered', number: 'GS-547821', issuer: 'Gas Safe Register', year: '2024', icon: '🔵', verified: true },
{ id: 'cert-002', name: 'CIPHE Member', number: 'CIPHE-22841', issuer: 'Chartered Institute of Plumbing', year: '2023', icon: '🟣', verified: true },
{ id: 'cert-003', name: 'Public Liability Insurance', number: 'PLI-9042811', issuer: 'Hiscox', year: 'Expires Apr 2027', icon: '🟢', verified: true },
{ id: 'cert-004', name: 'City & Guilds Level 3', number: 'CG-3-78421', issuer: 'City & Guilds', year: '2014', icon: '🟡', verified: true }];


const ENDORSEMENTS = [
{ id: 'end-001', name: 'Sarah Chen Electrical', trade: 'Electrician', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_10d60e496-1763295319842.png", avatarAlt: 'Sarah Chen headshot', comment: 'James is my go-to referral for all plumbing work. Reliable, fast, and clients love him.' },
{ id: 'end-002', name: 'Okafor Building Co.', trade: 'Builder', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1733cd6f4-1763294462474.png", avatarAlt: 'Mike Okafor headshot', comment: 'We work alongside James on extension projects regularly. Top quality every time.' },
{ id: 'end-003', name: 'Metro Tiling Specialists', trade: 'Tiler', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_179ebd6f2-1763294255544.png", avatarAlt: 'Carlos Diaz headshot', comment: 'Referred 6 clients to James this year alone. Never had a complaint.' }];


const SERVICE_AREAS = [
'SW4 — Clapham', 'SW8 — Battersea', 'SW11 — Wandsworth',
'SW12 — Balham', 'SW2 — Brixton', 'SE5 — Camberwell',
'SW9 — Stockwell', 'SW1V — Pimlico'];


export default function OverviewTab() {
  return (
    <div className="space-y-6">
      {/* About */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-6">
        <h2 className="font-display font-700 text-gray-900 text-lg mb-3">About James</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          With over 10 years of experience serving South West London, I provide a full range of plumbing and gas engineering services for homeowners and landlords. I'm Gas Safe registered (No. GS-547821) and carry full public liability insurance.
        </p>
        <p className="text-gray-600 leading-relaxed">
          I pride myself on transparent pricing, punctuality, and leaving your home exactly as I found it. Emergency callouts available 24/7 — I'll always aim to be with you within the hour. All work is guaranteed for 12 months.
        </p>
      </div>
      {/* Services & pricing */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Wrench size={18} className="text-plum-600" />
          <h2 className="font-display font-700 text-gray-900 text-lg">Services & Pricing</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {SERVICES?.map((svc) =>
          <div
            key={svc?.id}
            className="flex items-center justify-between p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">

              <div className="flex items-center gap-2">
                {svc?.popular &&
              <span className="w-1.5 h-1.5 rounded-full bg-mint-500 flex-shrink-0" />
              }
                <span className="text-sm text-gray-700 font-medium">{svc?.name}</span>
              </div>
              <span className="text-sm font-semibold text-plum-700 tabular-nums whitespace-nowrap ml-2">{svc?.price}</span>
            </div>
          )}
        </div>
        <p className="text-gray-400 text-xs mt-3">
          * All prices are indicative. Final quote provided after job assessment. VAT may apply.
        </p>
      </div>
      {/* Certifications */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={18} className="text-mint-600" />
          <h2 className="font-display font-700 text-gray-900 text-lg">Certifications & Insurance</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {CERTIFICATIONS?.map((cert) =>
          <div key={cert?.id} className="flex items-start gap-3 p-3.5 rounded-xl border border-gray-100 bg-gray-50">
              <span className="text-xl flex-shrink-0 mt-0.5">{cert?.icon}</span>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <p className="font-semibold text-sm text-gray-800">{cert?.name}</p>
                  {cert?.verified &&
                <CheckCircle2 size={13} className="text-mint-500 flex-shrink-0" />
                }
                </div>
                <p className="text-gray-500 text-xs">{cert?.issuer}</p>
                <p className="text-gray-400 text-xs tabular-nums">{cert?.number} · {cert?.year}</p>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Endorsements */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users size={18} className="text-plum-600" />
          <h2 className="font-display font-700 text-gray-900 text-lg">Endorsed by Providers</h2>
          <span className="ml-auto text-sm text-gray-400">18 endorsements</span>
        </div>
        <div className="space-y-3">
          {ENDORSEMENTS?.map((end) =>
          <div key={end?.id} className="flex items-start gap-3 p-3.5 rounded-xl bg-gray-50">
              <AppImage
              src={end?.avatar}
              alt={end?.avatarAlt}
              width={36}
              height={36}
              className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />

              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-semibold text-sm text-gray-800">{end?.name}</p>
                  <span className="text-xs text-gray-400">{end?.trade}</span>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">&ldquo;{end?.comment}&rdquo;</p>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Service areas */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <MapPin size={18} className="text-plum-600" />
          <h2 className="font-display font-700 text-gray-900 text-lg">Service Areas</h2>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {SERVICE_AREAS?.map((area) =>
          <span
            key={`area-${area}`}
            className="px-3 py-1.5 bg-plum-50 border border-plum-100 rounded-full text-sm text-plum-700 font-medium">

              {area}
            </span>
          )}
        </div>
        {/* Backend integration point: render Mapbox static map with service radius polygon */}
        <div className="h-40 rounded-xl bg-gradient-to-br from-plum-50 to-mint-50 flex items-center justify-center border border-gray-100">
          <p className="text-gray-400 text-sm">Service area map — Mapbox integration</p>
        </div>
      </div>
    </div>);

}